import {
  BottomSheet,
  type BottomSheetHandle,
} from "@-/dev.improve.haniverse.components.ui.bottom-sheet";
import { Button } from "@-/dev.improve.haniverse.components.ui.button";
import { Icon } from "@-/dev.improve.haniverse.components.ui.icon";
import { Text } from "@-/dev.improve.haniverse.components.ui.text";
import { GraphemeFontGlyph } from "@-/dev.improve.haniverse.routes.nav.app._components.morpheme.grapheme.glyph.font";
import { useGetGraphemeGlyph } from "@-/dev.improve.haniverse.routes.nav.app._lib.morpheme.grapheme.glyph/client";
import { SuspensePageBoundary } from "@-/dev.improve.haniverse.components.ui.suspense-page";
import { RootContext } from "@-/dev.improve.haniverse.routes._context";
import { useContextSelectors } from "@-/com.npmjs.use-context-selectors";
import type { MorphemeHrefObject } from "@-/dev.improve.haniverse.lib.morpheme.href.entities";
import { ActivityIndicator, Pressable, View } from "@-/com.npmjs.react-native";
import { type Ref } from "react";
import { GraphemePreviewSheetPanel } from "#grapheme-preview-sheet-panel";

/** Share of the window the sheet occupies. Fixed, so stepping between graphemes never resizes it. */
const SHEET_HEIGHT_RATIO = 0.75;
/** Room the sheet's own chrome takes out of that height: handle, header row, footer button. */
const SHEET_CHROME_HEIGHT = 148;

function GraphemePreviewSheetHeader({
  canGoBack,
  grapheme,
  onBack,
}: {
  canGoBack: boolean;
  grapheme: string;
  onBack: () => void;
}) {
  const getGraphemeGlyph = useGetGraphemeGlyph();

  return (
    <View className="flex-row items-center gap-2 border-b border-border pb-2">
      {canGoBack ? (
        <Pressable
          accessibilityLabel="Back to the previous character"
          accessibilityRole="button"
          className="-ml-1 size-8 items-center justify-center rounded-full"
          hitSlop={8}
          onPress={onBack}
          testID="grapheme-preview-sheet-back-button"
        >
          <Icon className="text-foreground" name="MdChevronLeft" size={24} />
        </Pressable>
      ) : null}
      <GraphemeFontGlyph
        className="text-[28px] leading-9 text-foreground"
        glyph={getGraphemeGlyph(grapheme, { uncolored: true })}
      />
      <Text className="text-[11px] font-bold tracking-widest text-muted-foreground uppercase">
        Preview
      </Text>
    </View>
  );
}

export function GraphemePreviewSheet({
  canGoBack,
  grapheme,
  morphemeHrefObject,
  onBack,
  onDismiss,
  onOpenGrapheme,
  ref,
}: {
  canGoBack: boolean;
  /** `null` before the sheet has ever been opened — nothing to render or fetch yet. */
  grapheme: string | null;
  morphemeHrefObject: MorphemeHrefObject;
  onBack: () => void;
  onDismiss: () => void;
  onOpenGrapheme: (grapheme: string) => void;
  ref?: Ref<BottomSheetHandle>;
}) {
  const [windowDimensions] = useContextSelectors(RootContext, (c) => c.windowDimensions);
  const sheetBodyHeight = Math.max(
    240,
    windowDimensions.height * SHEET_HEIGHT_RATIO - SHEET_CHROME_HEIGHT,
  );

  return (
    <BottomSheet
      footer={
        grapheme === null ? null : (
          <Button
            className="self-center rounded-full px-6"
            onPress={() => {
              onOpenGrapheme(grapheme);
            }}
            testID="grapheme-preview-sheet-open-button"
          >
            <Text className="font-semibold">Open {grapheme}</Text>
          </Button>
        )
      }
      onDismiss={onDismiss}
      ref={ref}
    >
      {grapheme === null ? null : (
        <View>
          <GraphemePreviewSheetHeader canGoBack={canGoBack} grapheme={grapheme} onBack={onBack} />
          <SuspensePageBoundary
            fallback={
              <View className="items-center justify-center" style={{ height: sheetBodyHeight }}>
                <ActivityIndicator />
              </View>
            }
            location={import.meta.location}
          >
            <GraphemePreviewSheetPanel
              // Remount per grapheme so the panel's internal scroll position and section state
              // start fresh instead of carrying over from the character stepped away from.
              grapheme={grapheme}
              height={sheetBodyHeight}
              key={grapheme}
              morphemeHrefObject={morphemeHrefObject}
            />
          </SuspensePageBoundary>
        </View>
      )}
    </BottomSheet>
  );
}
